/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef } from 'react';

import { jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';

import type { FieldBaseProps } from '../common/types';

import type { JiraSingleLineTextField_Platform$key } from './__generated__/JiraSingleLineTextField_Platform.graphql';
import { CustomTextField, type CustomTextFieldProps } from './CustomTextField';

export type Props = FieldBaseProps & {
	as?: CustomTextFieldProps['as'];
	field: JiraSingleLineTextField_Platform$key;
	isFullWidth?: CustomTextFieldProps['isFullWidth'];
	onChange: CustomTextFieldProps['onChange'];
};

export const JiraSingleLineTextField = ({
	as,
	fieldValue,
	field,
	isFieldEditable = true,
	isFullWidth,
	onChange,
}: Props): JSX.Element | null => {
	const data = useFragment<JiraSingleLineTextField_Platform$key>(
		graphql`
			fragment JiraSingleLineTextField_Platform on JiraSingleLineTextField {
				name
				fieldConfig {
					isEditable
					isRequired
				}
				text
			}
		`,
		field,
	);

	const { isRequired } = data?.fieldConfig || {};

	const isEditable = !!data?.fieldConfig?.isEditable && isFieldEditable;
	const defaultValue = typeof data?.text === 'string' ? data.text : '';
	const currentValue = typeof fieldValue === 'string' ? fieldValue : '';

	// Allows us to make sure we only fall back to default value once, on initial render
	const isFirstRenderRef = useRef(true);
	useEffect(() => {
		// If the passed down value is empty and a default value is available, tell the upstream
		if (!fieldValue && defaultValue != null && isFirstRenderRef.current) {
			onChange(defaultValue);
		}
		isFirstRenderRef.current = false;
	}, [fieldValue, defaultValue, onChange]);

	return (
		<CustomTextField
			as={as}
			isEditable={!!isEditable}
			isFullWidth={isFullWidth}
			isRequired={!!isRequired}
			currentValue={currentValue}
			fieldName={data?.name}
			onChange={onChange}
		/>
	);
};
